/**
 * additional_dialog_info の入力検証ゲート（RT1.0 / auto_rt 共通）。
 * フラグ未設定の項目は通過。
 *
 * - TelValid: tel1/tel2/tel3 すべて truthy かつ連結が /^[0-9０-９]+$/
 * - BirthYearValid: birthYear が /^[0-9]{4}$/
 * - NameValid: lastName かつ firstName
 * - MailValid: basicData.email が truthy（形式は見ない）
 * - MobileValid: SP 端末のみ許可（false/'PC' なら PC のみ）
 * - GenderValid: basicData.sex が入力済み
 * - BirthdayValid: birthYear / birthMonth / birthDay がすべて入力済み
 * - AddressValid / PostNumValid / JobHistoryValid / EducationalHistoryValid
 * - EducateHistoryNumValid: 卒年選択時に学歴実入力が1件以上
 * - UserRequestValid / NowNensyuValid / KibouShokushuValid / KibouGyoushuValid
 * - DomesticStudentValid: !formatData.foreignStudent
 * - exclude_mail_domains: userEmail に部分一致したら除外
 */

type AdditionalDialogInfoGateContext = {
  basicData?: Record<string, unknown>
  formatData?: unknown
  isMobileDevice?: boolean
  userEmail?: string | null
}

function isSpDevice(value: unknown): boolean {
  return (
    value === true ||
    value === 'SP' ||
    value === 'sp' ||
    value === 1 ||
    value === '1'
  )
}

function passesMobileDeviceGate(
  mobileValid: unknown,
  isMobile: boolean
): boolean {
  if (mobileValid === true || mobileValid === 'SP' || mobileValid === 'sp')
    return isMobile
  if (mobileValid === false || mobileValid === 'PC' || mobileValid === 'pc')
    return !isMobile
  return true
}

function normalizeDigits(value: unknown): string {
  return String(value ?? '').replace(/[０-９]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) - 0xfee0)
  )
}

function hasValidPostNumber(value: unknown): boolean {
  const normalized = normalizeDigits(value).replace(/-/g, '')
  return /^[0-9]{7}$/.test(normalized)
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0
  if (typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.some((v) => hasMeaningfulValue(v))
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    return Object.values(record).some((v) => hasMeaningfulValue(v))
  }
  return false
}

function hasFilledHistory(value: unknown): boolean {
  return Array.isArray(value) && value.some((item) => hasMeaningfulValue(item))
}

function passesEducateHistoryNumValid(
  formatData: Record<string, unknown>
): boolean {
  if (!hasMeaningfulValue(formatData.gradeYear)) return true
  return hasFilledHistory(formatData.educationalHistories)
}

/** RT1.0 グローバルゲートと同じ電話判定。 */
function passesTelValid(basicData: Record<string, unknown>): boolean {
  const tel1 = basicData.tel1
  const tel2 = basicData.tel2
  const tel3 = basicData.tel3
  const tel = [tel1, tel2, tel3].join('')
  return Boolean(tel1 && tel2 && tel3 && /^[0-9０-９]+$/.test(tel))
}

function passesBirthYearValid(basicData: Record<string, unknown>): boolean {
  return /^[0-9]{4}$/.test(String(basicData.birthYear ?? ''))
}

export function matchesAdditionalDialogInfoGate(
  add: Record<string, unknown>,
  ctx: AdditionalDialogInfoGateContext
): boolean {
  const bd = (ctx.basicData || {}) as Record<string, unknown>
  const fd = (ctx.formatData || {}) as Record<string, unknown>
  const isMobile = isSpDevice(ctx.isMobileDevice)

  if (!passesMobileDeviceGate(add.MobileValid, isMobile)) return false
  if (add.NameValid === true && !(bd.lastName && bd.firstName)) return false
  if (add.MailValid === true && !bd.email) return false
  if (add.TelValid === true && !passesTelValid(bd)) return false
  if (add.BirthYearValid === true && !passesBirthYearValid(bd)) return false
  if (add.GenderValid === true && !bd.sex) return false
  if (
    add.BirthdayValid === true &&
    !(bd.birthYear && bd.birthMonth && bd.birthDay)
  )
    return false
  if (add.AddressValid === true && !hasMeaningfulValue(bd.address1))
    return false
  if (add.PostNumValid === true && !hasValidPostNumber(bd.postNumber))
    return false
  if (add.JobHistoryValid === true && !hasFilledHistory(fd.jobHistories))
    return false
  if (
    add.EducationalHistoryValid === true &&
    !hasFilledHistory(fd.educationalHistories)
  )
    return false
  if (add.EducateHistoryNumValid === true && !passesEducateHistoryNumValid(fd))
    return false
  if (add.UserRequestValid === true && !hasMeaningfulValue(fd.request))
    return false
  if (add.NowNensyuValid === true && !hasMeaningfulValue(fd.nowNensyu))
    return false
  if (
    add.KibouShokushuValid === true &&
    !hasMeaningfulValue(bd.kibouShokushuCd)
  )
    return false
  if (add.KibouGyoushuValid === true && !hasMeaningfulValue(bd.kibouGyoushuCd))
    return false
  if (add.DomesticStudentValid === true && fd.foreignStudent) return false
  const domains = add.exclude_mail_domains
  if (Array.isArray(domains) && domains.length > 0) {
    const email = String(ctx.userEmail ?? '')
    if (
      domains.some(
        (d) => typeof d === 'string' && d !== '' && email.includes(d)
      )
    ) {
      return false
    }
  }
  return true
}
